import { sendAnalytics } from '@/services/analyticsService';
import { localStorageAsPromise } from '@/utils/localStorageAsPromise';
import type { AxiosInstance } from 'axios';
import type {
	PaywallEmailModalValueOptions,
	PaywallEmailModalKey
} from '../..';

import { paywallLocalStorage } from './paywallLocalStorage';

type PaywallInterceptorProps = {
	apiInstance: AxiosInstance;
};

export const paywallInterceptor = ({
	apiInstance
}: PaywallInterceptorProps) => {
	apiInstance.interceptors.response.use(
		request => request,
		async error => {
			const responseStatus = error.response.status;
			const isStatusForbidden = responseStatus === 403;

			const originalRequest = error.config;

			const paywallState = paywallLocalStorage.getItem();

			let {
				isAwaitingPayment = Boolean(paywallState === 'awaiting-payment')
			} = originalRequest;

			const paymentAccepted = Boolean(
				paywallState === 'payment-accepted'
			);

			if (isStatusForbidden && !isAwaitingPayment && !paymentAccepted) {
				isAwaitingPayment = true;

				paywallLocalStorage.setItem('awaiting-payment');

				sendAnalytics({
					type: 'track',
					eventName:
						'TrialFlow - User was unauthorized to continue and Payment modal opened',
					properties: {
						pathname: originalRequest.url
					}
				});

				await localStorageAsPromise<
					PaywallEmailModalKey,
					PaywallEmailModalValueOptions
				>({
					key: 'paywall-email-modal',
					expectedValues: [
						'payment-cancelled',
						'user-role-timeout',
						null
					]
				});

				isAwaitingPayment = false;

				if (!paywallLocalStorage.getItem()) {
					sendAnalytics({
						type: 'track',
						eventName:
							'TrialFlow - User completed the payment and the original request was resumed',
						properties: {
							pathname: originalRequest.url
						}
					});
					return apiInstance(originalRequest);
				}
			}

			paywallLocalStorage.removeItem();
			return Promise.resolve();
		}
	);
};
